import { IconButton, Tooltip, Typography } from '@mui/joy';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useProcessForm } from './ProcessesTab';

import { Card } from '~/components/ui/card';
import { LoadingWrapper } from '~/features/process-management/Common';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import { ProcessService } from '~/services/api/processes/endpoints';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';

export function ProcessTemplates() {
  const form = useProcessForm();
  const setProcess = () => form.setValue('process', '');

  const templatesQuery = ProcessService.endpoints.processTemplates.useQuery();

  return (
    <div className='flex flex-col w-full h-full overflow-auto gap-2'>
      <IconButton onClick={setProcess} className='w-fit'>
        <ArrowLeft />
      </IconButton>
      <Typography level='title-lg' className='ml-4'>
        <FormattedMessage defaultMessage='Process templates' />
      </Typography>
      <div className='grid grid-cols-4 gap-3'>
        <QueryResolver query={templatesQuery}>
          {(data) =>
            data.map((item) => (
              <TemplateItem
                key={item.id}
                template={{
                  id: item.id,
                  name: item.name,
                }}
              />
            ))
          }
        </QueryResolver>
      </div>
    </div>
  );
}

const TemplateItem = ({
  template,
}: {
  template: { id: number; name: string };
}) => {
  const intl = useIntl();
  const [showArrow, setShowArrow] = useState(false);
  const { referenceId } = useAppSelector((state) => state.reportData.report);
  const { createToastNotification } = useToastNotificationContext();

  const form = useProcessForm();

  const useProcessTemplate = useWrappedMutation(
    ProcessService.endpoints.useProcessTemplate.useMutation(),
  );

  const useProcesshandler = async () => {
    await useProcessTemplate.mutateAsync(
      {
        report_reference: referenceId,
        template_id: template.id,
      },
      {
        onSuccess: (res) => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Process Workflow Created Successfully ',
            }),
            'success',
          );
          form.setValue('workflowTab', `tab-${res.id}`);
          form.setValue('workflowId', res.id);
        },
        onError: (err) => {
          createToastNotification(getErrorMessage(err), 'error');
        },
      },
    );
  };

  const handleMouseOver = () => {
    setShowArrow(true);
  };

  const handleMouseOut = () => {
    setShowArrow(false);
  };

  return (
    <Tooltip title={template.name}>
      <LoadingWrapper loading={useProcessTemplate.state.isLoading}>
        <Card
          size='md'
          className='flex !flex-row justify-between items-center w-full bg-transparent cursor-pointer hover:bg-gray-400 hover:bg-opacity-15'
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          onClick={useProcesshandler}
        >
          <Typography level='body-md' className='truncate'>
            {template.name}
          </Typography>
          <div className='w-8'>{showArrow && <ArrowRight />}</div>
        </Card>
      </LoadingWrapper>
    </Tooltip>
  );
};
